/**
 * Área "Clientes" do painel: a lista de todo cadastro do portal, o filtro de origem, a escolha do
 * atendimento e a exclusão. Porte de `portal/admin.html:1774-1786` (HTML), `3541-3614` (a lista),
 * `4708-4815` (desenho e exclusão) e `5871-5918` (a escolha do atendimento).
 *
 * Linhas do inventário (`docs/PARIDADE-ADMIN.md`): 163 a 169. O recorte `?etapa=` vindo do Painel
 * é ACRÉSCIMO do sistema novo (regra de gráfico interativo), e não existe no legado.
 */

// Estado, efeito e memória do React.
import { useCallback, useEffect, useState } from 'react';
// As peças do Chassi: escolha, selo, aviso, botões, cartão que leva e os ícones.
import { Aviso, Botao, Campo, Cartao, IconeLixeira, Selecao, Selo } from '@complexity/ui';
// A criação do acesso da cliente, a MESMA que a sub-aba "Cliente Comunidade" usa: um caminho só.
import { criarAcessoClienteDoPortal } from '@studiolu/dados/areas/sistema';
// As leituras e a exclusão da área, e o tipo do lead oferecido na escolha.
import {
  excluirCliente,
  lerClientesNaEtapa,
  lerLeadsDaLista,
  lerLeadsParaEscolha,
  lerPerfis,
  lerQuestionariosDaLista,
  type LeadParaEscolha,
} from '@studiolu/dados/areas/clientes';
// Sessão, papel, fonte de dados, rota e navegação.
import { usePainel } from '../../contexto.js';
// O filtro que veio na rota.
import { filtroDaRota } from '../../rotas.js';
// O endereço da ficha com a origem, e a marca de quem volta da ficha.
import { ehVoltaDaFicha, enderecoDaFicha, semMarcaDeVolta } from '../ficha-cliente/comum-ficha/volta.js';
// O telefone com a máscara do legado e o atalho do WhatsApp Web, peças comuns do admin (A5).
import { formatarTelefone, mascararTelefoneDigitando } from '../../comum/formatos.js';
import { LinkWhatsapp } from '../../comum/whatsapp.js';
// O modal do admin atual: X, ESC, clique fora e o achado 5 para consulta.
import { ModalLegado } from '../../comum/modal-legado.js';
// A junção das três leituras e a ordem da escolha.
import { montarClientes, ordenarLeadsParaEscolha, type ClienteDaLista, type ListaDeClientes } from './regras.js';
// O nome dos produtos e das etapas da jornada, palavra por palavra do legado.
import { JORNADA_ETAPA_LABEL, nomeDoProduto } from '../ficha-cliente/comum-ficha/catalogo.js';

/** As origens do filtro, com os rótulos exatos do `<select>` do legado (`admin.html:1779-1782`). */
const OPCOES_ORIGEM = [
  { valor: 'funil', rotulo: 'Via Funil de vendas' },
  { valor: 'admin', rotulo: 'Cadastrada pela admin' },
  { valor: 'proprio', rotulo: 'Cadastro próprio (login/Google)' },
] as const;

/** O selo de origem: só as duas exceções ganham selo, "Via Funil" não (`admin.html:4721`, `4749-4751`). */
const ORIGEM_CLIENTE_LABEL: Readonly<Record<string, string>> = { admin: 'Cadastrada pela admin', proprio: 'Cadastro próprio' };

/**
 * O filtro de origem escolhido, guardado FORA do componente de propósito.
 *
 * No legado, o `<select>` é o mesmo elemento do começo ao fim da página: abrir uma ficha e voltar
 * pelo "Voltar" encontra o filtro como ficou. Aqui a lista desmonta ao abrir a ficha, e a escolha
 * guardada no módulo faz a volta encontrar o mesmo filtro. Recarregar a página zera os dois.
 */
let origemLembrada = '';

/**
 * A última lista montada, guardada FORA do componente pelo mesmo motivo do filtro.
 *
 * O "Voltar" da ficha do legado mostra a lista como ela estava, sem reler o banco
 * (`mostrar('tela-lista')`, `admin.html:6158` e `3332-3340`; achado 3 da verificação da A2). A
 * lista só relê ao entrar no painel (`3515`), no clique da aba de topo "Clientes" (`6449`) e
 * depois de excluir (`4814`). Aqui, quem volta da ficha chega com a marca de volta na rota
 * (`ficha-cliente/comum-ficha/volta.ts`) e recebe esta lista; o clique no menu chega sem a marca
 * e relê. Recarregar a página zera, como no legado.
 */
let listaGuardada: ListaDeClientes | null = null;

/**
 * O "Excluir" do modal: o primário com fundo de perigo, como `btn-primario btn-perigo`
 * (`admin.html:3027`), fundo `--erro` (`1519`) e texto `--acao-texto` (`378`), que é o token
 * `marca-contraste` nos dois temas (`packages/tokens/src/tokens.css`, comentários de `admin.html:67`
 * e `118`).
 *
 * POR QUE O `!`: o Chassi não tem variante de perigo (`VarianteBotao` em
 * `packages/ui/src/components/Button.tsx` tem seis papéis, e nenhum é o destrutivo), e o `Botao`
 * concatena as classes sem juntar as que disputam a mesma propriedade. Sem o `!`, a cor da variante
 * vence pela ordem do CSS, e foi assim que o "texto vermelho" anterior saiu sem vermelho nenhum
 * (achado 2 da verificação da A2, medido na 8010: o botão tinha a cor do texto comum e fundo
 * transparente).
 *
 * O VERMELHO FICA TAMBÉM NO HOVER, por decisão do fundador em 19/09/2026: toda ação de apagar,
 * excluir ou remover é vermelha em todo lugar. É a ÚNICA diferença proposital para o legado aqui:
 * lá o botão deixa de ser vermelho ao passar o mouse, porque `button:hover:not(:disabled)`
 * (`admin.html:388`) vale mais que `.btn-perigo`. Reproduzir esse defeito tiraria o sinal de perigo
 * justamente no instante em que o dedo está sobre a ação irreversível.
 */
const CLASSE_DO_EXCLUIR = '!border-erro !bg-erro !text-marca-contraste';

/**
 * A LIXEIRA da lista, com a cor de perigo em repouso e no hover, pela mesma decisão do fundador.
 *
 * POR QUE NÃO É O `BotaoIcone` DO CHASSI: ele tem `tom="perigo"`, e esse tom não pinta nada neste
 * repositório. As classes dele são `hover:bg-cx-perigo/10 hover:text-cx-perigo`
 * (`packages/ui/src/components/BotaoIcone.tsx:66`) e o preset desta plataforma não tem a cor
 * `perigo` nem o prefixo `cx-` (tem `erro`, em `packages/config/tailwind.preset.js:71`): o Tailwind
 * ignora a classe inexistente em silêncio, e o ícone herda a cor do pai. Foi o que o verificador
 * mediu na 8010, a cor do texto comum em vez do vermelho. A correção do Chassi nasce na plataforma,
 * e por isso vai como pedido ao principal; aqui o botão é o `Botao` de tamanho ícone com o token.
 *
 * No legado a lixeira é `--muted` em repouso e `--erro` no hover (`admin.html:583-589`); o vermelho
 * em repouso é a decisão de 19/09/2026, e está declarada na paridade.
 */
const CLASSE_DA_LIXEIRA = '!text-erro hover:!bg-erro-suave';

/** A escolha pendente do modal "Qual atendimento?": a cliente e os atendimentos oferecidos. */
interface EscolhaPendente {
  readonly cliente: ClienteDaLista;
  readonly leads: readonly LeadParaEscolha[];
}

export function TelaClientes() {
  const { dados, acesso, rota, navegar } = usePainel();
  // O recorte do Painel, quando a rota trouxe `?etapa=` (acréscimo, ver o cabeçalho).
  const etapa = filtroDaRota(rota).get('etapa');
  // Chegou pelo "Voltar" da ficha: a lista aparece como estava, sem reler.
  const voltaDaFicha = ehVoltaDaFicha(rota);
  // Onde a pessoa está, que a ficha recebe para o "Voltar" trazer de volta (com o recorte do Painel).
  const origemDaFicha = semMarcaDeVolta(rota);
  // A lista montada; `null` enquanto carrega, e a área fica vazia como no legado. Na volta da
  // ficha ela já nasce com a lista guardada, sem piscar vazia.
  const [lista, setLista] = useState<ListaDeClientes | null>(() => (voltaDaFicha ? listaGuardada : null));
  // As clientes com atendimento na etapa pedida; `null` sem recorte.
  const [naEtapa, setNaEtapa] = useState<ReadonlySet<string> | null>(null);
  // O filtro de origem, começando pelo que ficou da última visita.
  const [origem, setOrigem] = useState(origemLembrada);
  // A escolha de atendimento aberta, ou nenhuma.
  const [escolha, setEscolha] = useState<EscolhaPendente | null>(null);
  // A cliente com o modal de exclusão aberto, o texto de status e o botão travado.
  const [paraExcluir, setParaExcluir] = useState<ClienteDaLista | null>(null);
  const [statusExclusao, setStatusExclusao] = useState('');
  const [excluindo, setExcluindo] = useState(false);

  /**
   * INCLUIR CLIENTE A PARTIR DAQUI, pedido do fundador em 07/10/2026: *"na aba clientes do admin tem
   * que ter opção para a admin incluir novas clientes"*.
   *
   * NÃO NASCE CAMINHO NOVO DE CRIAÇÃO, e isso é de propósito: a criação continua sendo a mesma
   * Edge Function `criar-cliente-portal` que a sub-aba "Cliente Comunidade" já usava
   * (`admin.html:7455`), com os mesmos três campos e as mesmas mensagens. O que muda é só o LUGAR
   * de onde se chama. Um segundo caminho de criação divergiria do primeiro no dia em que um dos
   * dois ganhasse uma regra, e a que ficaria errada é a que ninguém olha.
   */
  const [criacaoAberta, setCriacaoAberta] = useState(false);
  const [novoNome, setNovoNome] = useState('');
  const [novoWhatsapp, setNovoWhatsapp] = useState('');
  const [novoEmail, setNovoEmail] = useState('');
  const [criando, setCriando] = useState(false);
  // O erro fica DENTRO do modal, com o formulário à vista para corrigir e tentar de novo.
  const [statusCriacao, setStatusCriacao] = useState('');
  // O sucesso fica FORA, acima da lista: o modal já fechou, e é na lista que a cliente nova aparece.
  const [avisoDaCriacao, setAvisoDaCriacao] = useState('');

  // "Apenas Consulta" tem todo `select` desabilitado (`admin.html:3393`).
  const somenteLeitura = acesso.papel === 'consulta';
  // A lixeira é só do administrador (`SELETOR_ACOES_EXCLUIR`, `admin.html:3375` e `3387-3389`).
  const podeExcluir = acesso.papel === 'administrador';

  /**
   * Carrega a lista como `carregarMapaClientesLead` (`admin.html:3541-3614`).
   *
   * Uma leitura que falha NÃO interrompe as outras: o legado escreve no console e segue com lista
   * vazia no lugar (`if (erro) console.error(...)`), e é por isso que uma falha na leitura dos
   * perfis mostra "Nenhuma cliente cadastrada ainda.". Reproduzido igual, com as mesmas mensagens
   * de console; distinguir falha de ausência é melhoria para o fundador decidir.
   */
  const carregar = useCallback(async () => {
    const perfis = await lerPerfis(dados);
    if (!perfis.ok) console.error('Erro ao carregar clientes:', perfis.erro);
    const listaDePerfis = perfis.ok ? perfis.dados : [];
    // Os ids vão para as duas leituras seguintes, como `userIds` no legado.
    const ids = listaDePerfis.map((p) => p.user_id);
    const questionarios = await lerQuestionariosDaLista(dados, ids);
    if (!questionarios.ok) console.error('Erro ao carregar dados do questionário:', questionarios.erro);
    const leads = await lerLeadsDaLista(dados, ids);
    if (!leads.ok) console.error('Erro ao carregar produtos dos clientes:', leads.erro);
    const montada = montarClientes(listaDePerfis, questionarios.ok ? questionarios.dados : [], leads.ok ? leads.dados : []);
    // Guardada para a volta da ficha, e mostrada.
    listaGuardada = montada;
    setLista(montada);
  }, [dados]);

  /**
   * "Criar acesso" a partir da lista: os mesmos três campos e as mesmas mensagens do legado
   * (`admin.html:7442` a `7468`), porque é a mesma função de banco por trás.
   *
   * A LISTA É RELIDA ao final, e não remendada em memória: a cliente nova nasce com linha em
   * `perfis_cliente` criada pela própria função, e inventar aqui uma linha equivalente faria a
   * tela mostrar um registro que pode não ser o que o banco guardou.
   */
  const criarCliente = useCallback(async () => {
    const nomeLimpo = novoNome.trim();
    const whatsappLimpo = novoWhatsapp.trim();
    const emailLimpo = novoEmail.trim();
    // A mesma validação do legado: os três obrigatórios, numa mensagem só.
    if (!nomeLimpo || !whatsappLimpo || !emailLimpo) {
      setStatusCriacao('Preencha nome, WhatsApp e e-mail.');
      return;
    }
    setCriando(true);
    setStatusCriacao('');
    const resultado = await criarAcessoClienteDoPortal(dados, {
      nome: nomeLimpo,
      whatsapp: whatsappLimpo,
      email: emailLimpo,
    });
    setCriando(false);
    // Falha de sistema (status fora de 2xx, rede): a mensagem do pacote, como nas outras telas.
    if (!resultado.ok) {
      setStatusCriacao(`Erro: ${resultado.erro.mensagem}`);
      return;
    }
    // 2xx com recusa de regra no corpo: o legado mostra "Erro: " e o motivo, e a tela não fecha.
    const corpo = resultado.dados;
    if (corpo?.error) {
      setStatusCriacao(`Erro: ${corpo.error}`);
      return;
    }
    // Criada: os campos voltam a vazio, o modal fecha e a lista relê do banco.
    setNovoNome('');
    setNovoWhatsapp('');
    setNovoEmail('');
    setCriacaoAberta(false);
    setStatusCriacao('');
    setAvisoDaCriacao(
      corpo?.aviso
        ? `Acesso criado, mas: ${corpo.aviso}`
        : 'Acesso criado! A cliente já recebeu o e-mail com a senha provisória.',
    );
    await carregar();
  }, [novoNome, novoWhatsapp, novoEmail, dados, carregar]);

  // A lista carrega ao abrir a área, e não na volta da ficha quando já há uma guardada.
  useEffect(() => {
    if (voltaDaFicha && listaGuardada) {
      setLista(listaGuardada);
      return;
    }
    void carregar();
  }, [carregar, voltaDaFicha]);

  // O recorte por etapa, quando pedido. Falha na leitura deixa o recorte vazio, e o aviso diz 0.
  useEffect(() => {
    if (!etapa) {
      setNaEtapa(null);
      return;
    }
    let vivo = true;
    void lerClientesNaEtapa(dados, etapa).then((r) => {
      if (!vivo) return;
      if (!r.ok) console.error('Erro ao carregar a etapa da jornada:', r.erro);
      setNaEtapa(new Set((r.ok ? r.dados : []).map((l) => l.user_id)));
    });
    return () => {
      vivo = false;
    };
  }, [dados, etapa]);

  // Trocar a origem vale na hora e fica lembrado para a volta da ficha.
  const trocarOrigem = (valor: string) => {
    origemLembrada = valor;
    setOrigem(valor);
  };

  /**
   * Clicar na cliente, como `abrirClienteComEscolhaDeLead` (`admin.html:5875-5889`): sem lead abre
   * a ficha sem lead, com um abre direto, com dois ou mais pergunta qual. Falha na leitura abre a
   * ficha sem lead, como o `catch` do legado.
   */
  const abrirCliente = async (cliente: ClienteDaLista) => {
    const r = await lerLeadsParaEscolha(dados, cliente.user_id);
    if (!r.ok) {
      console.error('Erro ao carregar leads da cliente:', r.erro);
      navegar(enderecoDaFicha(cliente.user_id, { de: origemDaFicha }));
      return;
    }
    if (r.dados.length === 0) {
      navegar(enderecoDaFicha(cliente.user_id, { de: origemDaFicha }));
      return;
    }
    // Um atendimento: a ficha já abre nele, com o lead no endereço.
    if (r.dados.length === 1) {
      navegar(enderecoDaFicha(cliente.user_id, { lead: r.dados[0]!.id, de: origemDaFicha }));
      return;
    }
    setEscolha({ cliente, leads: ordenarLeadsParaEscolha(r.dados) });
  };

  // Abre o modal de exclusão com o status limpo e o botão liberado (`admin.html:4787-4793`).
  const pedirExclusao = (cliente: ClienteDaLista) => {
    setStatusExclusao('');
    setExcluindo(false);
    setParaExcluir(cliente);
  };

  // O "Excluir" do modal (`admin.html:4798-4815`).
  const confirmarExclusao = async () => {
    if (!paraExcluir) return;
    setExcluindo(true);
    setStatusExclusao('Excluindo...');
    const r = await excluirCliente(dados, paraExcluir.user_id);
    if (!r.ok) {
      // A frase traduzida, como `traduzirErro(error.message)`, e o botão volta a valer.
      setStatusExclusao(r.erro.frase);
      setExcluindo(false);
      return;
    }
    setParaExcluir(null);
    await carregar();
  };

  // Enquanto carrega, nada além do filtro, como o `innerHTML` vazio do legado.
  const clientes = lista?.clientes ?? [];
  // O recorte do Painel vem antes do filtro de origem.
  const recortadas = naEtapa ? clientes.filter((c) => naEtapa.has(c.user_id)) : clientes;
  const filtradas = origem ? recortadas.filter((c) => c.origem === origem) : recortadas;

  return (
    <div className="flex flex-col gap-cx-4">
      {etapa && naEtapa && lista && (
        // Acréscimo: o recorte que veio do Painel, visível e com o caminho para desfazer.
        <Aviso
          tom="informacao"
          acao={
            <Botao variante="secundario" tamanho="sm" onClick={() => navegar('/clientes')}>
              Limpar filtro
            </Botao>
          }
        >
          Mostrando {recortadas.length} de {clientes.length} clientes na etapa &quot;
          {JORNADA_ETAPA_LABEL[etapa] ?? etapa}&quot; da jornada.
        </Aviso>
      )}

      {/* O resultado da criação, acima da lista, onde a cliente nova acaba de aparecer. */}
      {avisoDaCriacao && (
        <Aviso
          tom="sucesso"
          acao={
            <Botao variante="secundario" tamanho="sm" onClick={() => setAvisoDaCriacao('')}>
              Fechar
            </Botao>
          }
        >
          {avisoDaCriacao}
        </Aviso>
      )}

      {/*
        O FILTRO E O BOTÃO NA MESMA LINHA, com o botão à direita: é onde a ação principal de uma
        lista mora em toda plataforma grande, e assim ele não empurra a lista para baixo.
        `items-end` alinha o botão pela base do campo, que tem rótulo em cima e campo embaixo.
      */}
      <div className="flex flex-wrap items-end justify-between gap-cx-3">
        <div className="max-w-xs">
          <Selecao
            rotulo="Origem"
            vazio="Todas"
            opcoes={OPCOES_ORIGEM}
            value={origem}
            disabled={somenteLeitura}
            onChange={(e) => trocarOrigem(e.target.value)}
          />
        </div>
        {/*
          QUEM SÓ CONSULTA NÃO CRIA: o mesmo `somenteLeitura` que já trava o filtro e a exclusão
          nesta tela. Mostrar o botão e recusar depois seria pior que não mostrar.
        */}
        {!somenteLeitura && (
          <Botao
            variante="primario"
            onClick={() => {
              setStatusCriacao('');
              setCriacaoAberta(true);
            }}
            data-testid="botao-nova-cliente"
          >
            Nova cliente
          </Botao>
        )}
      </div>

      <ModalLegado
        aberto={criacaoAberta}
        aoFechar={() => setCriacaoAberta(false)}
        titulo="Nova cliente"
        testid="modal-nova-cliente"
        fechavel={!criando}
        rodape={
          <div className="flex flex-wrap justify-end gap-cx-2">
            <Botao variante="secundario" onClick={() => setCriacaoAberta(false)} disabled={criando}>
              Cancelar
            </Botao>
            <Botao
              variante="primario"
              onClick={() => void criarCliente()}
              disabled={criando}
              data-testid="confirmar-nova-cliente"
            >
              {criando ? 'Criando...' : 'Criar acesso'}
            </Botao>
          </div>
        }
      >
        <div className="flex flex-col gap-cx-3">
          {/*
            A FRASE DIZ O QUE VAI ACONTECER DE VERDADE, e não só o que o botão faz: a função cria o
            login e DISPARA um e-mail para a pessoa. Ação que sai do sistema e chega a alguém de
            fora precisa estar escrita antes do clique, e não descoberta depois.
          */}
          <p className="m-0 text-cx-sm text-texto-suave">
            Cria o acesso ao portal e envia para a cliente um e-mail com a senha provisória.
          </p>
          <Campo
            rotulo="Nome"
            obrigatorio
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
            data-testid="campo-nova-cliente-nome"
          />
          {/*
            A MÁSCARA É APLICADA NO QUE A PESSOA DIGITA, e não só na hora de mostrar: o fundador
            viu o campo com "619872787272" cru em 07/10/2026. `inputMode` abre o teclado numérico
            no celular, e o `placeholder` mostra a forma esperada antes do primeiro dígito.
          */}
          <Campo
            rotulo="WhatsApp"
            obrigatorio
            inputMode="tel"
            placeholder="(00) 00000-0000"
            value={novoWhatsapp}
            onChange={(e) => setNovoWhatsapp(mascararTelefoneDigitando(e.target.value))}
            data-testid="campo-nova-cliente-whatsapp"
          />
          <Campo
            rotulo="E-mail"
            type="email"
            obrigatorio
            value={novoEmail}
            onChange={(e) => setNovoEmail(e.target.value)}
            data-testid="campo-nova-cliente-email"
          />
          {/* O erro fica aqui dentro, junto do formulário que a pessoa vai corrigir. */}
          {statusCriacao && (
            <p className="m-0 text-cx-sm text-erro" role="alert" data-testid="status-nova-cliente">
              {statusCriacao}
            </p>
          )}
        </div>
      </ModalLegado>

      {lista && desenharLista()}

      <ModalLegado
        aberto={escolha !== null}
        aoFechar={() => setEscolha(null)}
        titulo="Qual atendimento?"
        testid="modal-selecao-lead"
      >
        {escolha && (
          <div className="flex flex-col gap-cx-3">
            {/* O nome no subtítulo, ou nada (`cliente.nome_completo || ''`, `admin.html:5892`). */}
            <p className="text-cx-sm text-texto-suave">{escolha.cliente.nome_completo || ''}</p>
            {escolha.leads.map((lead) => {
              const finalizado = lead.status === 'finalizada';
              const produtos = (lead.produtos ?? []).map(nomeDoProduto).join(', ') || '(sem produto definido)';
              const destino = enderecoDaFicha(escolha.cliente.user_id, { lead: lead.id, de: origemDaFicha });
              return (
                <Cartao key={lead.id} destino={destino} aoNavegar={navegar} nomeAcessivel={`${produtos}: ${finalizado ? 'Finalizado' : 'Em andamento'}`}>
                  <div className={`text-cx-sm font-medium ${finalizado ? 'text-texto-suave' : 'text-texto'}`}>{produtos}</div>
                  <div className="text-cx-xs text-texto-suave">{finalizado ? 'Finalizado' : 'Em andamento'}</div>
                </Cartao>
              );
            })}
          </div>
        )}
      </ModalLegado>

      <ModalLegado
        aberto={paraExcluir !== null}
        aoFechar={() => setParaExcluir(null)}
        titulo="Excluir cliente"
        testid="modal-excluir-cliente"
        rodape={
          <>
            <Botao variante="secundario" onClick={() => setParaExcluir(null)}>
              Cancelar
            </Botao>
            {/* O botão inteiro no vermelho de perigo, como o legado (`CLASSE_DO_EXCLUIR`). */}
            <Botao variante="primario" className={CLASSE_DO_EXCLUIR} disabled={excluindo} onClick={() => void confirmarExclusao()}>
              Excluir
            </Botao>
          </>
        }
      >
        <p className="text-cx-sm text-texto">
          {paraExcluir &&
            `Esta ação é irreversível: apaga o questionário, arquivos e login de "${paraExcluir.nome_completo || paraExcluir.email || 'essa cliente'}".`}
        </p>
        <p role="status" className="mt-cx-2 min-h-[1.25rem] text-cx-sm text-erro">
          {statusExclusao}
        </p>
      </ModalLegado>
    </div>
  );

  // A lista, com os dois vazios do legado (`admin.html:4731-4741`).
  function desenharLista() {
    if (clientes.length === 0) return <p className="text-cx-sm text-texto-suave">Nenhuma cliente cadastrada ainda.</p>;
    // Acréscimo: recorte do Painel sem ninguém, dito como recorte e não como cadastro vazio.
    if (recortadas.length === 0) return <p className="text-cx-sm text-texto-suave">Nenhuma cliente nesta etapa da jornada.</p>;
    if (filtradas.length === 0) return <p className="text-cx-sm text-texto-suave">Nenhuma cliente com essa origem.</p>;
    return (
      <ul className="m-0 flex list-none flex-col p-0" aria-label="Clientes">
        {filtradas.map((cliente) => (
          desenharItem(cliente)
        ))}
      </ul>
    );
  }

  // Um item da lista (`admin.html:4743-4779`).
  function desenharItem(cliente: ClienteDaLista) {
    const produtos = lista?.produtosPorCliente.get(cliente.user_id);
    const dataCadastro = new Date(cliente.criado_em).toLocaleDateString('pt-BR');
    // O separador antes da data muda com o telefone: " | " com telefone, " · " sem (`4747`).
    const separador = cliente.telefone ? ' | ' : ' · ';
    const selo = ORIGEM_CLIENTE_LABEL[cliente.origem];
    return (
      // O item inteiro abre a cliente, como o `addEventListener('click')` do `div` do legado.
      // O link do WhatsApp mora dentro do item e o clique nele SOBE até aqui, abrindo também a
      // ficha: é o que o legado faz (o `<a>` não para a propagação), e fica reproduzido.
      <li
        key={cliente.user_id}
        className="flex cursor-pointer items-start justify-between gap-cx-3 border-b border-borda px-cx-2 py-cx-3 last:border-b-0 hover:bg-superficie-afundada"
        onClick={() => void abrirCliente(cliente)}
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-cx-2 text-cx-base font-medium text-texto">
            {cliente.nome_completo || '(sem nome)'}
            {selo && <Selo tom="neutro">{selo}</Selo>}
          </div>
          <div className="break-words text-cx-sm text-texto-suave">
            {cliente.email || ''}
            {cliente.telefone && (
              <>
                {` · ${formatarTelefone(cliente.telefone)}`}
                <LinkWhatsapp telefone={cliente.telefone} />
              </>
            )}
            {`${separador}cadastrada em ${dataCadastro}`}
            {cliente.questionario_pendente ? ' · questionário pendente' : ''}
          </div>
          {produtos && (produtos.contratados.length > 0 || produtos.finalizados.length > 0) && (
            <div className="mt-cx-1 flex flex-wrap gap-cx-1">
              {produtos.contratados.map((p) => (
                <Selo key={`c-${p}`} tom="marca">
                  {nomeDoProduto(p)}
                </Selo>
              ))}
              {produtos.finalizados.map((p) => (
                <Selo key={`f-${p}`} tom="neutro">
                  {`${nomeDoProduto(p)} · finalizado`}
                </Selo>
              ))}
            </div>
          )}
        </div>
        {podeExcluir && (
          // Este invólucro para o clique antes do item, como o `e.stopPropagation()` da lixeira.
          <span className="shrink-0" onClick={(e) => e.stopPropagation()}>
            {/* O nome acessível e o título são os dois do legado (`admin.html:4762`). */}
            <Botao
              variante="fantasma"
              tamanho="icone"
              className={CLASSE_DA_LIXEIRA}
              aria-label="Excluir cliente"
              title="Excluir cliente"
              onClick={() => pedirExclusao(cliente)}
            >
              <IconeLixeira aria-hidden="true" width={16} height={16} />
            </Botao>
          </span>
        )}
      </li>
    );
  }
}

/*
 * O ATALHO DO WHATSAPP saiu daqui em 19/09/2026 e passou a vir de `src/comum/whatsapp.tsx`, a peça
 * comum do admin (frente A5). A cópia que existia aqui desenhava o ícone de conversa do Chassi, e a
 * peça comum desenha o MESMO caminho SVG do legado (`iconeWhatsapp`, `admin.html:7708-7714`): usar
 * a peça fecha a divergência do ícone nas linhas 165 e 177 da paridade, e é o que a regra de não
 * copiar peça comum manda fazer.
 */
